import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Alert, Form, ButtonGroup, Spinner } from 'react-bootstrap';
import { 
  FaFilePdf, FaSave, FaTimes, FaMousePointer, 
  FaFont, FaSignature, FaCheckSquare, FaCalendarAlt,
  FaUndo, FaRedo, FaMagic, FaDownload, FaExclamationTriangle
} from 'react-icons/fa';
import { PDFDocument, PDFPage, rgb, StandardFonts } from 'pdf-lib';
import { Document, Page, pdfjs } from 'react-pdf';
import axiosInstance from 'helpers/api';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface InlinePDFEditorProps {
  show: boolean;
  onHide: () => void;
  documentUrl: string;
  documentName: string;
  transactionData?: any; // Transaction data for auto-fill
  onSave?: (pdfBytes: Uint8Array) => void;
}

interface TextField {
  id: string;
  text: string;
  x: number;
  y: number;
  page: number;
  fontSize: number;
  color: string;
}

interface EditMode {
  type: 'text' | 'signature' | 'checkbox' | 'date' | null;
}

const InlinePDFEditor: React.FC<InlinePDFEditorProps> = ({
  show,
  onHide,
  documentUrl,
  documentName,
  transactionData,
  onSave
}) => {
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocument | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [editMode, setEditMode] = useState<EditMode>({ type: 'text' });
  const [textFields, setTextFields] = useState<TextField[]>([]);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [inputText, setInputText] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  
  // Refs for canvas interaction
  const pageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load PDF on mount
  useEffect(() => {
    loadPDF();
  }, [documentUrl]);

  const loadPDF = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch PDF bytes
      const response = await fetch(documentUrl);
      if (!response.ok) throw new Error('Failed to fetch PDF');
      
      const arrayBuffer = await response.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      
      // Load PDF document
      const doc = await PDFDocument.load(bytes);
      
      setPdfBytes(bytes);
      setPdfDoc(doc);
      setNumPages(doc.getPageCount());
      setLoading(false);
      
    } catch (err) {
      console.error('Error loading PDF:', err);
      setError('Failed to load PDF. Please try again.');
      setLoading(false);
    }
  };

  // Handle click on PDF to add text
  const handlePDFClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!editMode.type || !pageRef.current) return;
    
    const rect = pageRef.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) / scale;
    const y = (rect.height - (event.clientY - rect.top)) / scale; // PDF coordinates are bottom-up
    
    if (editMode.type === 'text') {
      const newField: TextField = {
        id: `field-${Date.now()}`,
        text: '',
        x: x,
        y: y,
        page: currentPage - 1,
        fontSize: 12,
        color: '#000000'
      };
      
      setTextFields([...textFields, newField]);
      setSelectedField(newField.id);
      setInputText('');
    }
  };

  // Update text field content
  const updateTextField = (id: string, text: string) => {
    setTextFields(fields => 
      fields.map(field => 
        field.id === id ? { ...field, text } : field
      )
    );
    setIsDirty(true);
  };

  // Auto-fill from transaction data
  const autoFillFields = useCallback(() => {
    if (!transactionData) return;
    
    const autoFillData: Record<string, string> = {
      'buyer_name': transactionData.client_name || '',
      'property_address': transactionData.property?.address || '',
      'price': transactionData.property?.price?.toString() || '',
      'date': new Date().toLocaleDateString(),
      'agent_name': transactionData.agent_name || '',
      'brokerage': 'KairosIQ Realty'
    };
    
    // Smart detection - look for common field patterns
    textFields.forEach(field => {
      Object.entries(autoFillData).forEach(([key, value]) => {
        if (field.text.toLowerCase().includes(key.replace('_', ' '))) {
          updateTextField(field.id, value);
        }
      });
    });
  }, [transactionData, textFields]);

  // Save PDF with modifications
  const savePDF = async () => {
    if (!pdfDoc) return;
    
    try {
      setSaving(true);
      
      // Create a copy of the PDF document
      const modifiedPdf = await PDFDocument.load(await pdfDoc.save());
      
      // Embed font
      const helveticaFont = await modifiedPdf.embedFont(StandardFonts.Helvetica);
      
      // Add text fields to the PDF
      textFields.forEach(field => {
        const page = modifiedPdf.getPages()[field.page];
        page.drawText(field.text, {
          x: field.x,
          y: field.y,
          size: field.fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
      });
      
      // Save the modified PDF
      const modifiedPdfBytes = await modifiedPdf.save();
      
      // Call onSave callback or download
      if (onSave) {
        onSave(modifiedPdfBytes);
      } else {
        // Download the file
        const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `edited_${documentName}`;
        a.click();
        URL.revokeObjectURL(url);
      }
      
      setIsDirty(false);
      setSaving(false);
      
      // Close modal after save
      setTimeout(() => {
        onHide();
      }, 1000);
      
    } catch (err) {
      console.error('Error saving PDF:', err);
      setError('Failed to save PDF. Please try again.');
      setSaving(false);
    }
  };

  // Render text overlay on PDF
  const renderTextOverlay = () => {
    return textFields
      .filter(field => field.page === currentPage - 1)
      .map(field => (
        <div
          key={field.id}
          style={{
            position: 'absolute',
            left: `${field.x * scale}px`,
            bottom: `${field.y * scale}px`,
            fontSize: `${field.fontSize * scale}px`,
            color: field.color,
            cursor: 'pointer',
            backgroundColor: selectedField === field.id ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
            padding: '2px 4px',
            borderRadius: '2px',
            border: selectedField === field.id ? '1px solid #007bff' : 'none'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedField(field.id);
            setInputText(field.text);
          }}
        >
          {field.text || '[ Click to edit ]'}
        </div>
      ));
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      size="xl"
      fullscreen="lg-down"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>
          <FaFilePdf className="me-2 text-danger" />
          PDF Editor - {documentName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ height: '70vh', overflow: 'hidden' }}>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p>Loading PDF...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <FaTimes className="me-2" />
            {error}
          </Alert>
        )}

        {!loading && !error && pdfBytes && (
          <div className="d-flex h-100">
            {/* Left Panel - Tools */}
            <div className="bg-light p-3" style={{ width: '200px', borderRight: '1px solid #dee2e6' }}>
              <h6 className="mb-3">Tools</h6>
              
              <ButtonGroup vertical className="w-100 mb-4">
                <Button 
                  variant={editMode.type === 'text' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setEditMode({ type: 'text' })}
                  className="text-start mb-2"
                >
                  <FaFont className="me-2" />
                  Add Text
                </Button>
                <Button 
                  variant="outline-primary"
                  size="sm"
                  disabled
                  className="text-start mb-2"
                >
                  <FaSignature className="me-2" />
                  Signature (Soon)
                </Button>
                <Button 
                  variant="outline-primary"
                  size="sm"
                  disabled
                  className="text-start mb-2"
                >
                  <FaCheckSquare className="me-2" />
                  Checkbox (Soon)
                </Button>
                <Button 
                  variant="outline-primary"
                  size="sm"
                  disabled
                  className="text-start"
                >
                  <FaCalendarAlt className="me-2" />
                  Date (Soon)
                </Button>
              </ButtonGroup>

              <h6 className="mb-3">Actions</h6>
              <ButtonGroup vertical className="w-100">
                <Button 
                  variant="outline-success"
                  size="sm"
                  onClick={autoFillFields}
                  className="text-start mb-2"
                >
                  <FaMagic className="me-2" />
                  Auto-Fill
                </Button>
                <Button 
                  variant="success"
                  size="sm"
                  onClick={savePDF}
                  disabled={saving || !isDirty}
                  className="text-start"
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Save PDF
                    </>
                  )}
                </Button>
              </ButtonGroup>
            </div>

            {/* Center - PDF Viewer */}
            <div className="flex-grow-1 p-3 overflow-auto" style={{ backgroundColor: '#e9ecef' }}>
              <div className="text-center mb-2">
                <ButtonGroup size="sm">
                  <Button 
                    variant="outline-secondary" 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button variant="outline-secondary" disabled>
                    Page {currentPage} of {numPages}
                  </Button>
                  <Button 
                    variant="outline-secondary"
                    disabled={currentPage === numPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </ButtonGroup>
              </div>

              <div 
                ref={pageRef}
                onClick={handlePDFClick}
                style={{ 
                  position: 'relative',
                  display: 'inline-block',
                  cursor: editMode.type ? 'crosshair' : 'default',
                  margin: '0 auto',
                  boxShadow: '0 0 10px rgba(0,0,0,0.3)'
                }}
              >
                <Document file={pdfBytes}>
                  <Page 
                    pageNumber={currentPage}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
                {renderTextOverlay()}
              </div>
            </div>

            {/* Right Panel - Properties */}
            <div className="bg-light p-3" style={{ width: '250px', borderLeft: '1px solid #dee2e6' }}>
              <h6 className="mb-3">Text Properties</h6>
              
              {selectedField && (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Text Content</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={inputText}
                      onChange={(e) => {
                        setInputText(e.target.value);
                        updateTextField(selectedField, e.target.value);
                      }}
                      placeholder="Enter text..."
                    />
                  </Form.Group>
                  
                  <Button 
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      setTextFields(fields => fields.filter(f => f.id !== selectedField));
                      setSelectedField(null);
                      setInputText('');
                      setIsDirty(true);
                    }}
                  >
                    <FaTimes className="me-1" />
                    Delete Field
                  </Button>
                </Form>
              )}
              
              {!selectedField && (
                <Alert variant="info" className="small">
                  Click on the PDF to add text fields
                </Alert>
              )}

              {/* Smart suggestions based on transaction data */}
              {transactionData && (
                <div className="mt-4">
                  <h6 className="mb-2">Quick Fill</h6>
                  <div className="d-grid gap-1">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => setInputText(transactionData.client_name || '')}
                    >
                      {transactionData.client_name}
                    </Button>
                    {transactionData.property?.address && (
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => setInputText(transactionData.property.address)}
                      >
                        {transactionData.property.address}
                      </Button>
                    )}
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => setInputText(new Date().toLocaleDateString())}
                    >
                      Today's Date
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            {isDirty && (
              <small className="text-warning">
                <FaExclamationTriangle className="me-1" />
                You have unsaved changes
              </small>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} disabled={saving}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={savePDF}
              disabled={saving || !isDirty}
              className="ms-2"
            >
              {saving ? 'Saving...' : 'Save & Close'}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default InlinePDFEditor;